/**
 * DOM creation helpers — replaces JSX.
 */

type Attrs = Record<string, any>;

/**
 * Create an HTML element with attributes, styles, events, and children.
 *
 * Attribute keys:
 * - "style"    → merged into element.style
 * - "className" → element.className
 * - "on..."   → addEventListener (e.g. onClick → "click")
 * - "dataset"  → Object.assign(element.dataset, value)
 * - anything else → setAttribute
 */
export function el(
  tag: string,
  attrs?: Attrs | null,
  ...children: (Node | string | null | undefined | false)[]
): HTMLElement {
  const element = document.createElement(tag);
  if (attrs) applyAttrs(element, attrs);
  appendChildren(element, children);
  return element;
}

/**
 * Create an SVG element.
 */
export function svg(
  tag: string,
  attrs?: Attrs | null,
  ...children: (Node | string | null | undefined | false)[]
): SVGElement {
  const element = document.createElementNS(
    "http://www.w3.org/2000/svg",
    tag
  );
  if (attrs) {
    for (const [key, value] of Object.entries(attrs)) {
      if (value == null || value === false) continue;
      if (key === "style" && typeof value === "object") {
        applyStyles(element as unknown as HTMLElement, value);
      } else {
        element.setAttribute(key, String(value));
      }
    }
  }
  appendChildren(element as unknown as HTMLElement, children);
  return element;
}

function applyAttrs(element: HTMLElement, attrs: Attrs): void {
  for (const [key, value] of Object.entries(attrs)) {
    if (value == null || value === false) continue;

    if (key === "style" && typeof value === "object") {
      applyStyles(element, value);
    } else if (key === "className") {
      element.className = value;
    } else if (key === "dataset" && typeof value === "object") {
      Object.assign(element.dataset, value);
    } else if (key.startsWith("on") && typeof value === "function") {
      const event = key.slice(2).toLowerCase();
      element.addEventListener(event, value);
    } else if (key === "checked" || key === "disabled" || key === "readOnly") {
      (element as any)[key] = value;
    } else {
      element.setAttribute(key, String(value));
    }
  }
}

/**
 * CSS properties that take a unitless number, mirroring React's CSSProperty
 * list. For these we MUST NOT append "px" — doing so produces invalid CSS
 * (e.g. `font-weight: 400px`) and the browser falls back to the user-agent
 * default (which is what made <h3> purpose descriptions render as bold).
 */
const UNITLESS_PROPS = new Set([
  "animationIterationCount",
  "aspectRatio",
  "borderImageOutset",
  "borderImageSlice",
  "borderImageWidth",
  "boxFlex",
  "boxFlexGroup",
  "boxOrdinalGroup",
  "columnCount",
  "columns",
  "flex",
  "flexGrow",
  "flexPositive",
  "flexShrink",
  "flexNegative",
  "flexOrder",
  "fontWeight",
  "gridArea",
  "gridRow",
  "gridRowEnd",
  "gridRowSpan",
  "gridRowStart",
  "gridColumn",
  "gridColumnEnd",
  "gridColumnSpan",
  "gridColumnStart",
  "lineClamp",
  "lineHeight",
  "WebkitLineClamp",
  "opacity",
  "order",
  "orphans",
  "scale",
  "tabSize",
  "widows",
  "zIndex",
  "zoom",
]);

/**
 * Apply a camelCase style object to an element.
 *
 * Numeric values are interpreted as pixels (matching React's behavior) EXCEPT
 * for the unitless properties listed above, which are passed through as-is.
 */
export function applyStyles(
  element: HTMLElement,
  styles: Record<string, any>
): void {
  for (const [key, value] of Object.entries(styles)) {
    if (value == null) continue;
    const stringValue =
      typeof value === "number" && !UNITLESS_PROPS.has(key)
        ? `${value}px`
        : String(value);
    (element.style as any)[key] = stringValue;
  }
}

function appendChildren(
  parent: HTMLElement,
  children: (Node | string | null | undefined | false)[]
): void {
  for (const child of children) {
    if (child == null || child === false) continue;
    if (typeof child === "string") {
      parent.appendChild(document.createTextNode(child));
    } else {
      parent.appendChild(child);
    }
  }
}

/**
 * Remove all children from an element.
 */
export function clearElement(element: HTMLElement): void {
  while (element.firstChild) {
    element.removeChild(element.firstChild);
  }
}
